import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MENU_GROUPS, MenuItem, istSichtbar } from '../config/menu';
import menueService from '../services/menueService';
import kundeService from '../services/kundeService';
import berichtService from '../services/berichtService';
import protokollService from '../services/protokollService';
import aufgabeService from '../services/aufgabeService';
import { beratungService } from '../services/beratungService';
import wareService from '../services/wareService';
import { bauteilService } from '../services/elektrolisteService';
import { potenzialService } from '../services/potenzialService';
import userService from '../services/userService';
import { markiereNeu } from '../utils/ankunft';
import { Icon, IconName, Spinner } from './ui';

/** Ein Treffer: Titel fett, darunter eine gedämpfte Zeile; `text` ist das, was durchsucht wird. */
interface Eintrag {
  key: string;
  titel: string;
  unter?: string;
  text: string;
  pfad: string;
  /** Listen ohne Detailseite: der Eintrag leuchtet nach dem Sprung an seinem Platz auf. */
  markiere?: string;
}

interface Quelle {
  key: string;          // = Menü-Schlüssel, damit ausgeblendete Bereiche auch nicht durchsucht werden
  label: string;
  icon: IconName;
  laden: () => Promise<Eintrag[]>;
}

const MAX_JE_QUELLE = 5;

const datum = (iso?: string) =>
  iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const verbinde = (...teile: (string | number | undefined | null | false)[]) =>
  teile.filter((t) => t !== undefined && t !== null && t !== false && t !== '').join(' · ');

const name = (nachname?: string, vorname?: string) => [nachname, vorname].filter(Boolean).join(', ');

const kuerzen = (s: string | undefined, n = 80) => {
  const einzeilig = (s || '').replace(/\s+/g, ' ').trim();
  return einzeilig.length > n ? `${einzeilig.slice(0, n - 1)}…` : einzeilig;
};

const suchtext = (...teile: (string | number | undefined | null)[]) =>
  teile.filter((t) => t !== undefined && t !== null && t !== '').join(' ').toLowerCase();

const QUELLEN: Quelle[] = [
  {
    key: 'kunden', label: 'Kunden', icon: 'Kunden',
    laden: async () => (await kundeService.getAll()).map((k) => ({
      key: `kunde-${k.id}`,
      titel: name(k.nachnameFirma, k.vorname),
      unter: verbinde(k.strasse, [k.plz, k.ort].filter(Boolean).join(' '), k.mobiltelefon, k.active === false && 'inaktiv'),
      text: suchtext(k.nachnameFirma, k.vorname, k.strasse, k.plz, k.ort, k.mobiltelefon, k.email, k.wisoKundennummer),
      pfad: `/kunden/${k.id}`,
    })),
  },
  {
    key: 'berichte', label: 'Berichte', icon: 'Bericht',
    laden: async () => (await berichtService.getAll()).map((b) => ({
      key: `bericht-${b.id}`,
      titel: name(b.kundeNachnameFirma, b.kundeVorname) || 'Bericht',
      unter: verbinde(datum(b.datum), kuerzen(b.ausgefuehrteArbeiten)),
      text: suchtext(b.kundeNachnameFirma, b.kundeVorname, datum(b.datum), b.ausgefuehrteArbeiten, b.hinweise, b.erstelltVon, b.statusName),
      pfad: `/berichte/${b.id}/bearbeiten`,
    })),
  },
  {
    key: 'protokolle', label: 'Inbetriebnahme', icon: 'Protokoll',
    laden: async () => (await protokollService.getAll()).map((p) => ({
      key: `protokoll-${p.id}`,
      titel: name(p.kundeNachnameFirma, p.kundeVorname) || 'Protokoll',
      unter: verbinde(datum(p.datum), p.entwurf && 'Entwurf', kuerzen(p.maengel)),
      text: suchtext(p.kundeNachnameFirma, p.kundeVorname, datum(p.datum), p.teilnehmer, p.maengel, p.erstelltVon),
      pfad: `/protokolle/${p.id}/bearbeiten`,
    })),
  },
  {
    key: 'aufgaben', label: 'Aufgaben', icon: 'Aufgaben',
    laden: async () => (await aufgabeService.getAll()).map((a) => ({
      key: `aufgabe-${a.id}`,
      titel: a.thema,
      unter: verbinde(a.statusName, a.zugewiesenAnName, a.faelligAm && `fällig ${datum(a.faelligAm)}`),
      text: suchtext(a.thema, a.beschreibung, a.zugewiesenAnName, a.erstelltVonName, a.statusName),
      pfad: `/aufgaben?id=${a.id}`,
    })),
  },
  {
    key: 'beratungstermine', label: 'Beratungstermine', icon: 'Kalender',
    laden: async () => (await beratungService.getAll()).map((t) => ({
      key: `termin-${t.id}`,
      titel: name(t.nachname, t.vorname),
      unter: verbinde(t.telefon, t.plz, t.beraterName, t.erledigt && 'erledigt'),
      text: suchtext(t.nachname, t.vorname, t.telefon, t.email, t.plz, t.bemerkung, t.beraterName),
      pfad: t.erledigt ? '/beratungstermine?filter=alle' : '/beratungstermine',
      markiere: `termin-${t.id}`,
    })),
  },
  {
    key: 'waren', label: 'Artikel', icon: 'Paket',
    laden: async () => (await wareService.getAll()).map((w) => ({
      key: `ware-${w.id}`,
      titel: w.artikelbezeichnung,
      unter: w.einheit,
      text: suchtext(w.artikelbezeichnung, w.einheit),
      pfad: '/waren',
      markiere: `ware-${w.id}`,
    })),
  },
  {
    key: 'bauteile', label: 'Elektrobauteile', icon: 'Blitz',
    laden: async () => (await bauteilService.getAll()).map((b) => ({
      key: `bauteil-${b.id}`,
      titel: b.titel,
      unter: kuerzen(b.eigenschaften.map((e) => `${e.schluessel}: ${e.wert}`).join(', ')),
      text: suchtext(b.titel, ...b.eigenschaften.map((e) => e.wert)),
      pfad: '/bauteile',
      markiere: `bauteil-${b.id}`,
    })),
  },
  {
    key: 'potenziale', label: 'Potenziale', icon: 'Ziel',
    laden: async () => (await potenzialService.getAll()).map((p) => ({
      key: `potenzial-${p.id}`,
      titel: [p.strasse, p.hausnummer].filter(Boolean).join(' ') || 'Potenzial',
      unter: verbinde([p.plz, p.ort].filter(Boolean).join(' '), `${Math.round(p.scoreProzent)} %`),
      text: suchtext(p.strasse, p.hausnummer, p.plz, p.ort, p.gemeinde),
      pfad: `/potenziale?id=${p.id}`,
    })),
  },
  {
    key: 'users', label: 'Benutzer', icon: 'Schild',
    laden: async () => (await userService.getAll()).map((u) => ({
      key: `user-${u.id}`,
      titel: `${u.firstName} ${u.lastName}`,
      unter: verbinde(u.username, u.role === 'ADMIN' ? 'Administrator' : 'Benutzer'),
      text: suchtext(u.firstName, u.lastName, u.username),
      pfad: '/users',
      markiere: `user-${u.id}`,
    })),
  },
];

const alleMenuepunkte = (): MenuItem[] =>
  MENU_GROUPS.flatMap((g) => g.items.flatMap((i) => [i, ...(i.unterpunkte || [])]));

interface Gruppe { key: string; label: string; icon: IconName; treffer: Eintrag[]; mehr: number }

/**
 * Suche über alles (Seiten, Kunden, Berichte, Protokolle, Aufgaben, Termine, Artikel, Bauteile,
 * Potenziale, Benutzer) – ganz oben im Dashboard, von überall per Strg/⌘ + K erreichbar.
 * Die Daten kommen beim ersten Fokus; gefiltert wird im Browser. Jedes Wort muss vorkommen,
 * Treffer am Titelanfang stehen oben. Pfeiltasten wählen, Enter öffnet.
 */
const GlobaleSuche: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const eingabeRef = useRef<HTMLInputElement>(null);
  const [eingabe, setEingabe] = useState('');
  const [offen, setOffen] = useState(false);
  const [auswahl, setAuswahl] = useState(0);
  const [sichtbarkeit, setSichtbarkeit] = useState<Record<string, boolean> | null>(null);
  const [daten, setDaten] = useState<Record<string, Eintrag[]>>({});
  const [ladend, setLadend] = useState(0);
  const geladen = useRef(false);

  // Strg/⌘ + K (aus dem Layout) landet hier mit `state.suche`.
  const sucheState = (location.state as { suche?: number } | null)?.suche;
  useEffect(() => {
    if (sucheState) eingabeRef.current?.focus();
  }, [sucheState]);

  useEffect(() => {
    menueService.getSichtbarkeit().then(setSichtbarkeit).catch(() => setSichtbarkeit({}));
  }, []);

  const quellen = useMemo(() => {
    if (!sichtbarkeit) return [];
    const menue = new Map(alleMenuepunkte().map((m) => [m.key, m]));
    return QUELLEN.filter((q) => {
      const m = menue.get(q.key);
      return m ? istSichtbar(m, admin, sichtbarkeit) : true;
    });
  }, [sichtbarkeit, admin]);

  const laden = useCallback(() => {
    if (geladen.current || quellen.length === 0) return;
    geladen.current = true;
    setLadend(quellen.length);
    quellen.forEach((q) => {
      // Einzelne Bereiche dürfen fehlschlagen (z. B. keine Berechtigung) – der Rest bleibt durchsuchbar.
      q.laden()
        .then((eintraege) => setDaten((d) => ({ ...d, [q.key]: eintraege })))
        .catch(() => {})
        .finally(() => setLadend((n) => n - 1));
    });
  }, [quellen]);

  // Kam der Fokus schon, bevor die Sichtbarkeit da war, jetzt nachladen.
  useEffect(() => { if (offen) laden(); }, [offen, laden]);

  const seiten = useMemo<Eintrag[]>(() => {
    if (!sichtbarkeit) return [];
    return alleMenuepunkte()
      .filter((m) => istSichtbar(m, admin, sichtbarkeit))
      .map((m) => ({ key: `seite-${m.key}`, titel: m.label, text: m.label.toLowerCase(), pfad: m.path }));
  }, [sichtbarkeit, admin]);

  const woerter = useMemo(() => eingabe.toLowerCase().split(/\s+/).filter(Boolean), [eingabe]);

  const gruppen = useMemo<Gruppe[]>(() => {
    if (woerter.length === 0) return [];
    const erstes = woerter[0];
    const filtern = (liste: Eintrag[], max: number) => {
      const passend = liste
        .filter((e) => woerter.every((w) => e.text.includes(w)))
        .map((e) => ({ e, rang: e.titel.toLowerCase().startsWith(erstes) ? 0 : 1 }))
        .sort((a, b) => a.rang - b.rang)
        .map(({ e }) => e);
      return { treffer: passend.slice(0, max), mehr: Math.max(0, passend.length - max) };
    };
    const ergebnis: Gruppe[] = [{ key: 'seiten', label: 'Seiten', icon: 'Raster', ...filtern(seiten, 3) }];
    quellen.forEach((q) => {
      if (daten[q.key]) ergebnis.push({ key: q.key, label: q.label, icon: q.icon, ...filtern(daten[q.key], MAX_JE_QUELLE) });
    });
    return ergebnis.filter((g) => g.treffer.length > 0);
  }, [woerter, seiten, quellen, daten]);

  const flach = useMemo(() => gruppen.flatMap((g) => g.treffer), [gruppen]);

  useEffect(() => { setAuswahl(0); }, [eingabe]);

  const oeffnen = (e: Eintrag) => {
    if (e.markiere) markiereNeu(e.markiere);
    setOffen(false);
    eingabeRef.current?.blur();
    navigate(e.pfad);
  };

  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (eingabe) setEingabe(''); else eingabeRef.current?.blur();
    } else if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setOffen(true);
      setAuswahl((i) => Math.min(i + 1, flach.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setAuswahl((i) => Math.max(i - 1, 0));
    } else if (ev.key === 'Enter' && flach[auswahl]) {
      ev.preventDefault();
      oeffnen(flach[auswahl]);
    }
  };

  const ausgewaehlt = flach[auswahl];
  const zeigeListe = offen && woerter.length > 0;
  const istMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div className="relative w-full">
      <Icon.Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
      <input
        ref={eingabeRef}
        type="search"
        value={eingabe}
        onChange={(e) => { setEingabe(e.target.value); setOffen(true); }}
        onFocus={() => { setOffen(true); laden(); }}
        onBlur={() => setOffen(false)}
        onKeyDown={onKeyDown}
        placeholder="Kunden, Berichte, Aufgaben, Artikel … suchen"
        aria-label="Suche"
        role="combobox"
        aria-expanded={zeigeListe}
        aria-controls="globale-suche-treffer"
        aria-activedescendant={zeigeListe && ausgewaehlt ? `suche-${ausgewaehlt.key}` : undefined}
        className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-11 pr-16 text-sm shadow-sm placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
      />
      {!eingabe && (
        <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-gray-200 px-1.5 py-0.5 font-sans text-xs text-gray-400 md:block">
          {istMac ? '⌘' : 'Strg'} K
        </kbd>
      )}

      {zeigeListe && (
        <div
          id="globale-suche-treffer"
          role="listbox"
          // mousedown würde sonst erst den Fokus nehmen und die Liste schließen, bevor der Klick ankommt
          onMouseDown={(e) => e.preventDefault()}
          className="ui-pop absolute left-0 right-0 top-full z-30 mt-1.5 max-h-[70vh] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
        >
          {gruppen.map((g) => {
            const GruppenIcon = Icon[g.icon];
            return (
              <div key={g.key} role="group" aria-label={g.label} className="py-1">
                <div className="flex items-center gap-1.5 px-4 pb-1 pt-1.5 text-xs font-semibold text-gray-500">
                  <GruppenIcon className="h-3.5 w-3.5" />
                  {g.label}
                  {g.mehr > 0 && <span className="font-normal text-gray-400">und {g.mehr} weitere</span>}
                </div>
                {g.treffer.map((e) => {
                  const aktiv = e === ausgewaehlt;
                  return (
                    <div
                      key={e.key}
                      id={`suche-${e.key}`}
                      role="option"
                      aria-selected={aktiv}
                      onMouseMove={() => setAuswahl(flach.indexOf(e))}
                      onClick={() => oeffnen(e)}
                      className={`cursor-pointer px-4 py-2 ${aktiv ? 'bg-brand-50' : ''}`}
                    >
                      <div className="truncate text-sm font-semibold text-gray-900">{e.titel}</div>
                      {e.unter && <div className="truncate text-xs text-gray-500">{e.unter}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {ladend > 0 ? (
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400"><Spinner className="h-3.5 w-3.5" />Wird geladen …</div>
          ) : gruppen.length === 0 && (
            <p className="px-4 py-4 text-center text-sm text-gray-500">Nichts passt zu „{eingabe.trim()}“.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobaleSuche;
